import { useMemo, useRef } from "react";
import { QBoxLayout } from "@coreapp/CoreApp/QBoxLayout";
import { Flex, Grid, Text } from "@theorgchartdev/storybook";
import { GetWSMgr } from "@/Utils/StateLib";
import { useBoxStylePreview } from "@/appComponents/Navigation/RightSidebar/BoxStyles/useBoxStylePreview";
import { bestContrastColor } from "@/Utils/colorContrast";
import { appstr } from "@coreapp/utils/appstr";

type BrandStyleCanvasPreviewProps = {
  fill: string;
  border: string;
};

const BASE_PRIMARY_TEXT = "#2B2B35";
const BASE_SECONDARY_TEXT = "#3D3D4A";
const LIGHT_PRIMARY_TEXT = "#F5F5F5";
const LIGHT_SECONDARY_TEXT = "#EAEAEB";

function getBrandingTextColors(fillColor: string): { primary: string; secondary: string } {
  return {
    primary: bestContrastColor(fillColor, LIGHT_PRIMARY_TEXT, BASE_PRIMARY_TEXT),
    secondary: bestContrastColor(fillColor, LIGHT_SECONDARY_TEXT, BASE_SECONDARY_TEXT)
  };
}

function normalizeHex(color?: string): string {
  return (color || "").trim().toUpperCase();
}

function isPrimaryBrandText(color: string): boolean {
  return color === BASE_PRIMARY_TEXT || color === LIGHT_PRIMARY_TEXT;
}

function isSecondaryBrandText(color: string): boolean {
  return color === BASE_SECONDARY_TEXT || color === LIGHT_SECONDARY_TEXT;
}

function applyBrandColorsToLayout(layout: QBoxLayout, fill: string, border: string): QBoxLayout {
  const clone = layout.Clone({ includeId: true, includeLayoutName: true });
  clone.fBoxFillColor = fill;
  clone.fBoxBorderColor = border;
  const { primary: textPrimary, secondary: textSecondary } = getBrandingTextColors(fill);

  // Match backend token behavior: only replace known branded base text colors.
  const cells = clone.fCellList || [];
  for (const cell of cells) {
    if (!cell || typeof cell.SetFontColor !== "function") continue;
    if (typeof cell.IsPicture === "function" && cell.IsPicture()) continue;

    const current = normalizeHex(
      typeof cell.GetFontColor === "function" ? cell.GetFontColor() : undefined
    );
    if (isPrimaryBrandText(current)) {
      cell.SetFontColor(textPrimary);
      continue;
    }
    if (isSecondaryBrandText(current)) {
      cell.SetFontColor(textSecondary);
    }
  }

  return clone;
}

function BrandPreviewCanvas({ layout }: { layout: QBoxLayout | null }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useBoxStylePreview(canvasRef, layout);
  return <canvas ref={canvasRef} />;
}

const BrandStyleCanvasPreview = ({ fill, border }: BrandStyleCanvasPreviewProps) => {
  const previewLayouts = useMemo(() => {
    const bsMgr = GetWSMgr()?.GetBoxStylesMgr?.();
    if (!bsMgr) return [];

    const systemLayouts: QBoxLayout[] = Array.isArray(bsMgr.fSysBoxStyles)
      ? (bsMgr.fSysBoxStyles as QBoxLayout[])
      : [];
    const reactive: QBoxLayout[] = systemLayouts.filter(
      (layout: QBoxLayout) => layout?.fBrandReactive === true
    );

    return reactive.map(layout => applyBrandColorsToLayout(layout, fill, border));
  }, [fill, border]);

  return (
    <Flex
      direction="column"
      gap="24px"
      padding="16px"
      overflow="auto"
      className="brand-style-canvas-preview ocn-list-scrollbar"
    >
      <Text modifier="medium">{appstr("Preview employee box styles")}</Text>
      <Grid columns={3} columnGap={"24px"} rowGap={"24px"}>
        {previewLayouts.map((layout, i) => (
          <Flex direction="column" key={i} gap="8px" alignItems="center" justifyContent="center">
            <BrandPreviewCanvas layout={layout} />
            <Text size="small">{layout.GetBoxStyleName()}</Text>
          </Flex>
        ))}
      </Grid>
    </Flex>
  );
};

export default BrandStyleCanvasPreview;
